import { type RefObject, useCallback, useEffect, useState } from "react";

type FullDoc = Pick<Document, "addEventListener" | "removeEventListener"> & {
  fullscreenElement?: Element | null;
  webkitFullscreenElement?: Element | null;
  fullscreenEnabled?: boolean;
  webkitFullscreenEnabled?: boolean;
  exitFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => void;
};
type FullElement = { requestFullscreen?: () => Promise<void>; webkitRequestFullscreen?: () => void };

export const FULLSCREEN_EVENTS = ["fullscreenchange", "webkitfullscreenchange"] as const;

/** The fullscreen element of the page, also in an older Safari. */
export const fullscreenOf = (doc: FullDoc): Element | null => doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;

/** False when the browser cannot put an element in fullscreen, for example Safari on an iPhone. */
export const canFullscreen = (doc: FullDoc): boolean => Boolean(doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled);

/** Put the element in fullscreen, or take the page out of fullscreen when it is in. */
export async function toggleFullscreen(doc: FullDoc, element: FullElement): Promise<"enter" | "exit" | "none"> {
  if (fullscreenOf(doc)) {
    if (doc.exitFullscreen) await doc.exitFullscreen();
    else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
    else return "none";
    return "exit";
  }
  if (element.requestFullscreen) await element.requestFullscreen();
  else if (element.webkitRequestFullscreen) element.webkitRequestFullscreen();
  else return "none";
  return "enter";
}

/** The fullscreen state of a map wrap. The wrap goes into fullscreen, so its box and its cards stay on the map. */
export function useMapFullscreen(wrap: RefObject<HTMLElement>) {
  const [on, setOn] = useState(false);
  const [supported] = useState(() => typeof document !== "undefined" && canFullscreen(document));
  useEffect(() => {
    const changed = () => {
      const now = fullscreenOf(document) !== null && fullscreenOf(document) === wrap.current;
      setOn(now);
      console.info(`map fullscreen: ${now ? "on" : "off"}`);
    };
    for (const name of FULLSCREEN_EVENTS) document.addEventListener(name, changed);
    return () => {
      for (const name of FULLSCREEN_EVENTS) document.removeEventListener(name, changed);
    };
  }, [wrap]);
  const toggle = useCallback(() => {
    const element = wrap.current;
    if (!element) return;
    toggleFullscreen(document, element).catch((e: unknown) => console.warn("map fullscreen: the browser refused", e));
  }, [wrap]);
  return { on, supported, toggle };
}

/** Our fullscreen button in the place of the Google control, for the whole map wrap. */
export function MapFullscreenButton({ wrap, testId }: { wrap: RefObject<HTMLElement>; testId?: string }) {
  const { on, supported, toggle } = useMapFullscreen(wrap);
  if (!supported) return null;
  const label = on ? "Exit full screen" : "Show the map in full screen";
  return (
    <button type="button" className="map-fullscreen" aria-label={label} title={label} aria-pressed={on} data-testid={testId ?? "map-fullscreen"} onClick={toggle}>
      <svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true">
        <path d={on ? "M7 2v5H2M11 2v5h5M7 16v-5H2M11 16v-5h5" : "M2 7V2h5M16 7V2h-5M2 11v5h5M16 11v5h-5"} />
      </svg>
    </button>
  );
}
